/**
 * Research report viewer (admin-only) — a full-viewport overlay that lists the
 * markdown reports from the backend's reports dir and renders the selected one.
 *
 * Read-only and live: the backend serves whatever `.md` files are in
 * `settings.reports_dir` right now, so "Refresh" (or reopening) reflects any
 * report re-run with no redeploy. Gated by `reports.view` (admin only) — the
 * button that mounts this is gated too, and the backend enforces the cap with a
 * 403 regardless.
 */
import { useCallback, useEffect, useState } from "react";
import { useCan } from "@/state/hooks";
import { api, ApiError } from "@/api/client";
import { CAP } from "@/api/capabilities";
import { fmtAgo } from "@/utils/format";
import type { ReportMeta } from "@/api/types";
import { renderMarkdown } from "./markdown";
import styles from "./ReportsViewer.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ReportsViewer({ open, onClose }: Props) {
  const canView = useCan(CAP.reportsView);
  const [reports, setReports] = useState<ReportMeta[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [html, setHtml] = useState<string>("");
  const [listError, setListError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [loadingBody, setLoadingBody] = useState(false);

  const loadList = useCallback(async () => {
    setListError(null);
    try {
      const res = await api.adminReports();
      setReports(res.reports);
      // Keep the current selection if it survived a refresh, else pick the first.
      setActive((cur) =>
        cur && res.reports.some((r) => r.name === cur)
          ? cur
          : res.reports[0]?.name ?? null,
      );
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : "Failed to load reports");
      setReports([]);
    }
  }, []);

  // (Re)load the list whenever the panel opens.
  useEffect(() => {
    if (open && canView) void loadList();
  }, [open, canView, loadList]);

  // Fetch + render the active report's markdown.
  useEffect(() => {
    if (!open || !active) {
      setHtml("");
      return;
    }
    let cancelled = false;
    setLoadingBody(true);
    setBodyError(null);
    api
      .adminReport(active)
      .then((res) => {
        if (!cancelled) setHtml(renderMarkdown(res.markdown));
      })
      .catch((err) => {
        if (!cancelled)
          setBodyError(err instanceof ApiError ? err.message : "Failed to load report");
      })
      .finally(() => {
        if (!cancelled) setLoadingBody(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, active]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !canView) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Research reports">
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.panel}>
        <aside className={styles.sidebar}>
          <div className={styles.brandRow}>
            <span className={styles.brand}>Research reports</span>
            <button
              type="button"
              className={styles.refresh}
              onClick={() => void loadList()}
              title="Reload the report list"
            >
              ↻
            </button>
          </div>
          <p className={styles.sub}>{reports.length} reports</p>
          {listError ? <p className={styles.err}>{listError}</p> : null}
          <nav className={styles.nav}>
            {reports.map((r) => (
              <button
                key={r.name}
                type="button"
                className={`${styles.item} ${r.name === active ? styles.itemActive : ""}`}
                onClick={() => setActive(r.name)}
              >
                <span className={styles.itemTitle}>{r.title}</span>
                <span className={styles.itemFile}>{r.name}</span>
                <span className={styles.itemMeta}>updated {fmtAgo(r.modified)}</span>
              </button>
            ))}
            {!reports.length && !listError ? (
              <p className={styles.empty}>
                No reports found. Generate them, then publish into the reports dir.
              </p>
            ) : null}
          </nav>
        </aside>

        <main className={styles.content}>
          <div className={styles.topbar}>
            <span className={styles.topTitle}>
              {reports.find((r) => r.name === active)?.title ?? "Reports"}
            </span>
            <button type="button" className={styles.close} onClick={onClose} title="Close (Esc)">
              ✕
            </button>
          </div>
          <div className={styles.doc}>
            {loadingBody ? (
              <p className={styles.muted}>Loading…</p>
            ) : bodyError ? (
              <p className={styles.err}>{bodyError}</p>
            ) : active ? (
              // Safe: markdown.ts HTML-escapes all text before formatting.
              <div dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p className={styles.muted}>Select a report.</p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
